// Chakra imports
import {
  AvatarGroup,
  Avatar,
  Box,
  Button,
  Flex,
  Icon,
  Image,
  Link,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
// Custom components
import Card from "components/card/Card.js";
// Assets
import React, { useState, useEffect } from "react";
import { IoHeart, IoHeartOutline } from "react-icons/io5";
import { likeBlog, unlikeBlog, updateBlogPartial } from "services/blogService";
import { useToast } from "@chakra-ui/react";

export default function NFT(props) {
  const { image, name, author, bidders, download, currentbid, blogId, isLiked: initialIsLiked, likes: initialLikes, status, onPublish, onUnpublish } = props;
  const [like, setLike] = useState(initialIsLiked || false);
  const [likesCount, setLikesCount] = useState(initialLikes || 0);
  const [liking, setLiking] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const toast = useToast();
  const textColor = useColorModeValue("navy.700", "white");
  const textColorBid = useColorModeValue("brand.500", "white");

  // Sync with props when they change
  useEffect(() => {
    if (initialIsLiked !== undefined) {
      setLike(initialIsLiked);
    }
    if (initialLikes !== undefined) {
      setLikesCount(initialLikes);
    }
  }, [initialIsLiked, initialLikes]);

  // Handle like/unlike
  const handleLike = async () => {
    if (!blogId || liking) return;

    setLiking(true);
    const wasLiked = like;
    const previousLikes = likesCount;

    // Optimistic update
    setLike(!like);
    setLikesCount(wasLiked ? likesCount - 1 : likesCount + 1);

    try {
      let result;
      if (wasLiked) {
        result = await unlikeBlog(blogId);
      } else {
        result = await likeBlog(blogId);
      }

      if (!result.success) {
        // Revert on error
        setLike(wasLiked);
        setLikesCount(previousLikes);
        toast({
          title: 'Error',
          description: result.error || 'Failed to update like',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Update with actual data from server
        if (result.data) {
          setLikesCount(result.data.likes || likesCount);
          setLike(result.data.is_liked || false);
        }
      }
    } catch (error) {
      // Revert on error
      setLike(wasLiked);
      setLikesCount(previousLikes);
      toast({
        title: 'Error',
        description: 'Failed to update like. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLiking(false);
    }
  };

  // Handle publish
  const handlePublish = async () => {
    if (!blogId || publishing || (status && status.toUpperCase() !== 'DRAFT')) return;

    setPublishing(true);
    try {
      const result = await updateBlogPartial(blogId, { status: 'PUBLISHED' });

      if (result.success) {
        toast({
          title: 'Success',
          description: 'Blog published successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        // Call the onPublish callback to refresh the blog list
        if (onPublish) {
          onPublish(blogId);
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to publish blog',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to publish blog. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setPublishing(false);
    }
  };

  // Handle unpublish
  const handleUnpublish = async () => {
    if (!blogId || unpublishing || (status && status.toUpperCase() !== 'PUBLISHED')) return;

    setUnpublishing(true);
    try {
      const result = await updateBlogPartial(blogId, { status: 'DRAFT' });

      if (result.success) {
        toast({
          title: 'Success',
          description: 'Blog unpublished successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        // Call the onUnpublish callback to refresh the blog list
        if (onUnpublish) {
          onUnpublish(blogId);
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to unpublish blog',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to unpublish blog. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUnpublishing(false);
    }
  };

  return (
    <Card p='20px'>
      <Flex direction={{ base: "column" }} justify='center'>
        <Box mb={{ base: "20px", "2xl": "20px" }} position='relative'>
          <Image
            src={image}
            w={{ base: "100%", "3xl": "100%" }}
            h={{ base: "100%", "3xl": "100%" }}
            borderRadius='20px'
          />
          <Button
            position='absolute'
            bg='white'
            _hover={{ bg: "whiteAlpha.900" }}
            _active={{ bg: "white" }}
            _focus={{ bg: "white" }}
            p='0px !important'
            top='14px'
            right='14px'
            borderRadius='50%'
            minW='36px'
            h='36px'
            onClick={handleLike}
            isLoading={liking}
            disabled={liking || !blogId}>
            <Icon
              transition='0.2s linear'
              w='20px'
              h='20px'
              as={like ? IoHeart : IoHeartOutline}
              color='brand.500'
            />
          </Button>
        </Box>
        <Flex flexDirection='column' justify='space-between' h='100%'>
          <Flex
            justify='space-between'
            direction={{
              base: "row",
              md: "column",
              lg: "row",
              xl: "column",
              "2xl": "row",
            }}
            mb='auto'>
            <Flex direction='column'>
              <Text
                color={textColor}
                fontSize={{
                  base: "xl",
                  md: "lg",
                  lg: "lg",
                  xl: "lg",
                  "2xl": "md",
                  "3xl": "lg",
                }}
                mb='5px'
                fontWeight='bold'
                me='14px'>
                {name}
              </Text>
              <Text
                color='secondaryGray.600'
                fontSize={{
                  base: "sm",
                }}
                fontWeight='400'
                me='14px'>
                {author}
              </Text>
            </Flex>
            <AvatarGroup
              max={3}
              color={textColorBid}
              size='sm'
              mt={{
                base: "0px",
                md: "10px",
                lg: "0px",
                xl: "10px",
                "2xl": "0px",
              }}
              fontSize='12px'>
              {bidders.map((avt, key) => (
                <Avatar key={key} src={avt} />
              ))}
            </AvatarGroup>
          </Flex>
          <Flex
            align='start'
            justify='space-between'
            direction={{
              base: "row",
              md: "column",
              lg: "row",
              xl: "column",
              "2xl": "row",
            }}
            mt='25px'>
            <Text fontWeight='700' fontSize='sm' color={textColorBid}>
              {currentbid}
            </Text>
            <Flex
              gap='8px'
              mt={{
                base: "0px",
                md: "10px",
                lg: "0px",
                xl: "10px",
                "2xl": "0px",
              }}>
              {status && status.toUpperCase() === 'DRAFT' && (
                <Button
                  variant='solid'
                  colorScheme='green'
                  color='white'
                  fontSize='sm'
                  fontWeight='500'
                  borderRadius='70px'
                  px='20px'
                  py='5px'
                  onClick={handlePublish}
                  isLoading={publishing}
                  loadingText='Publishing...'>
                  Publish
                </Button>
              )}
              {status && status.toUpperCase() === 'PUBLISHED' && (
                <Button
                  variant='solid'
                  colorScheme='orange'
                  color='white'
                  fontSize='sm'
                  fontWeight='500'
                  borderRadius='70px'
                  px='20px'
                  py='5px'
                  onClick={handleUnpublish}
                  isLoading={unpublishing}
                  loadingText='Unpublishing...'>
                  Unpublish
                </Button>
              )}
              {download && (
                <Link href={download}>
                  <Button
                    variant='darkBrand'
                    color='white'
                    fontSize='sm'
                    fontWeight='500'
                    borderRadius='70px'
                    px='24px'
                    py='5px'>
                    View Blog
                  </Button>
                </Link>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
}
